import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, Plus, X as XIcon } from 'lucide-react';

/**
 * Collapsible profile row editor.
 *
 * Collapsed: icon + label + current value (or "Not set")
 * Expanded: chips (single or multi-select) + optional notes + Save/Cancel
 *
 * All in-flight edits are local; parent only receives final Save payload.
 */
export const RowEditor = ({
  icon: Icon,
  label,
  options = [],
  value,                 // string (single) | string[] (multi) | null
  notes = '',
  multi = false,
  text = false,          // true = single-line free text (no chips)
  maxLength,
  placeholder,
  readOnly = false,
  onSave,                // async ({ value, notes }) => void
}) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draftValue, setDraftValue] = useState(value ?? (multi ? [] : ''));
  const [draftNotes, setDraftNotes] = useState(notes || '');
  const [customDraft, setCustomDraft] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);

  useEffect(() => {
    setDraftValue(value ?? (multi ? [] : ''));
    setDraftNotes(notes || '');
    setCustomDraft('');
    setShowCustomInput(false);
  }, [value, notes, multi, open]);

  const hasValue = multi
    ? Array.isArray(value) && value.length > 0
    : !!(value && value.toString().trim());

  const displayValue = multi
    ? (hasValue ? (value || []).join(', ') : 'Not set')
    : (hasValue ? value : 'Not set');

  const toggleChip = (opt) => {
    if (multi) {
      setDraftValue(prev =>
        (prev || []).includes(opt)
          ? prev.filter(v => v !== opt)
          : [...(prev || []), opt]
      );
    } else {
      setDraftValue(prev => (prev === opt ? '' : opt));
    }
  };

  const commitCustom = () => {
    const v = customDraft.trim().slice(0, 80);
    if (!v) {
      setShowCustomInput(false);
      setCustomDraft('');
      return;
    }
    if (multi) {
      setDraftValue(prev => (prev || []).includes(v) ? prev : [...(prev || []), v]);
    } else {
      setDraftValue(v);
    }
    setCustomDraft('');
    setShowCustomInput(false);
  };

  // All chips to render = preset options + any currently-selected custom values
  const currentSelections = multi
    ? (draftValue || [])
    : (draftValue ? [draftValue] : []);
  const customSelections = currentSelections.filter(v => !options.includes(v));
  const renderedOptions = [...options, ...customSelections];

  const handleSave = async () => {
    setSaving(true);
    try {
      const cleanNotes = (draftNotes || '').trim();
      await onSave({
        value: multi ? (draftValue || []) : (draftValue || null),
        notes: cleanNotes || null,
      });
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setDraftValue(value ?? (multi ? [] : ''));
    setDraftNotes(notes || '');
    setOpen(false);
  };

  return (
    <div
      className="rounded-2xl border border-charcoal/5 bg-white/60 overflow-hidden transition-colors"
      data-testid={`row-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
    >
      <button
        type="button"
        disabled={readOnly}
        onClick={() => !readOnly && setOpen(o => !o)}
        className={`w-full flex items-start gap-3 text-left p-4 ${readOnly ? 'cursor-default' : 'hover:bg-orange-primary/5'} transition-colors`}
      >
        {Icon && (
          <div className="w-9 h-9 rounded-lg bg-orange-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon size={16} className="text-orange-primary" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-charcoal/50 text-xs uppercase tracking-wider mb-1">
            {label}
          </div>
          <div className={`text-sm leading-snug break-words ${hasValue ? 'text-charcoal' : 'text-charcoal/30'}`}>
            {displayValue}
          </div>
          {notes && hasValue && (
            <div className="text-xs text-charcoal/50 mt-1 italic">“{notes}”</div>
          )}
        </div>
        {!readOnly && (open
          ? <ChevronUp size={16} className="text-charcoal/40 mt-2 flex-shrink-0" />
          : <ChevronDown size={16} className="text-charcoal/40 mt-2 flex-shrink-0" />
        )}
      </button>

      <AnimatePresence initial={false}>
        {open && !readOnly && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="border-t border-charcoal/5"
          >
            <div className="p-4 space-y-3">
              {text ? (
                <input
                  type="text"
                  value={draftValue || ''}
                  onChange={e => setDraftValue(e.target.value.slice(0, maxLength || 200))}
                  placeholder={placeholder || ''}
                  maxLength={maxLength || 200}
                  data-testid={`text-input-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  className="w-full px-3 py-2.5 rounded-lg border border-charcoal/10 bg-white text-sm text-charcoal focus:outline-none focus:border-orange-primary/50 transition-colors"
                />
              ) : (
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-2">
                    {renderedOptions.map(opt => {
                      const selected = multi
                        ? (draftValue || []).includes(opt)
                        : draftValue === opt;
                      const isCustom = !options.includes(opt);
                      return (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => toggleChip(opt)}
                          className={`px-3 py-1.5 rounded-full text-xs transition-colors inline-flex items-center gap-1 ${
                            selected
                              ? 'bg-orange-primary text-white'
                              : 'bg-charcoal/5 text-charcoal/70 hover:bg-charcoal/10'
                          } ${isCustom && selected ? 'ring-1 ring-orange-primary/40' : ''}`}
                          data-testid={`chip-${opt.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                        >
                          {opt}
                          {isCustom && selected && (
                            <XIcon size={11} className="opacity-70" />
                          )}
                        </button>
                      );
                    })}

                    {showCustomInput ? (
                      <div className="inline-flex items-center gap-1 rounded-full bg-orange-primary/10 border border-orange-primary/30 px-2 py-0.5">
                        <input
                          type="text"
                          autoFocus
                          value={customDraft}
                          onChange={e => setCustomDraft(e.target.value.slice(0, 80))}
                          onKeyDown={e => {
                            if (e.key === 'Enter') { e.preventDefault(); commitCustom(); }
                            if (e.key === 'Escape') {
                              setShowCustomInput(false);
                              setCustomDraft('');
                            }
                          }}
                          onBlur={commitCustom}
                          placeholder="Type and press Enter"
                          maxLength={80}
                          data-testid={`custom-input-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                          className="bg-transparent text-xs text-charcoal placeholder:text-charcoal/40 focus:outline-none min-w-[140px]"
                        />
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setShowCustomInput(true)}
                        data-testid={`add-custom-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                        className="px-3 py-1.5 rounded-full text-xs inline-flex items-center gap-1 border border-dashed border-charcoal/20 text-charcoal/50 hover:border-orange-primary/40 hover:text-orange-primary transition-colors"
                      >
                        <Plus size={11} /> Add your own
                      </button>
                    )}
                  </div>
                </div>
              )}

              {!text && (
                <textarea
                  value={draftNotes}
                  onChange={e => setDraftNotes(e.target.value)}
                  placeholder="Optional notes"
                  rows={2}
                  data-testid={`notes-input-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  className="w-full px-3 py-2.5 rounded-lg border border-charcoal/10 bg-white text-sm text-charcoal placeholder:text-charcoal/30 focus:outline-none focus:border-orange-primary/50 transition-colors resize-none"
                />
              )}

              <div className="flex items-center gap-2 pt-1">
                <button
                  type="button"
                  disabled={saving}
                  onClick={handleSave}
                  className="px-4 py-1.5 rounded-full bg-orange-primary text-white text-sm font-medium hover:bg-orange-primary/90 transition-colors disabled:opacity-50"
                  data-testid={`save-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  disabled={saving}
                  onClick={handleCancel}
                  className="px-4 py-1.5 rounded-full bg-charcoal/5 text-charcoal/70 text-sm hover:bg-charcoal/10 transition-colors"
                  data-testid={`cancel-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/**
 * Card wrapper for the 4 big profile sections.
 */
export const SectionCard = ({ title, subtitle, children, testId }) => (
  <section
    className="rounded-3xl bg-white/40 border border-charcoal/5 p-5 sm:p-6 space-y-4"
    data-testid={testId}
  >
    <header>
      <h2 className="font-serif text-2xl text-charcoal">{title}</h2>
      {subtitle && (
        <p className="text-sm text-charcoal/50 mt-1">{subtitle}</p>
      )}
    </header>
    <div className="space-y-2.5">{children}</div>
  </section>
);

/**
 * 7-dot bipolar scale. Clicking a dot saves instantly.
 * In read-only mode, only the selected dot is highlighted; others are muted.
 */
export const ScaleRow = ({
  id,
  left,
  right,
  value,                // 1..7 or null
  onChange,             // async (value | null) => void
  readOnly = false,
  compareValue = null,  // the viewer's own answer, overlaid as a ring
}) => {
  const [saving, setSaving] = useState(null);      // which index is being written
  const [justSaved, setJustSaved] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const handleClick = async (idx) => {
    if (readOnly) return;
    const newValue = idx + 1;
    setSaving(newValue);
    try {
      await onChange(newValue);
      setJustSaved(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setJustSaved(false), 1400);
    } finally {
      setSaving(null);
    }
  };

  const handleSkip = async () => {
    if (readOnly) return;
    setSaving(-1);
    try {
      await onChange(null);
      setJustSaved(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setJustSaved(false), 1400);
    } finally {
      setSaving(null);
    }
  };

  return (
    <div
      className="py-3 border-b border-charcoal/5 last:border-b-0"
      data-testid={`scale-${id}`}
    >
      <div className="flex items-center gap-3 sm:gap-4">
        <span className="flex-1 text-xs sm:text-sm text-charcoal/70 text-right leading-snug">
          {left}
        </span>

        <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
          {Array.from({ length: 7 }).map((_, i) => {
            const selected = value === i + 1;
            const isSaving = saving === i + 1;
            const isMine = compareValue === i + 1;
            return (
              <button
                key={`scale-${id}-dot-${i}`}
                type="button"
                disabled={readOnly}
                onClick={() => handleClick(i)}
                data-testid={`scale-${id}-dot-${i + 1}`}
                aria-label={`Option ${i + 1} of 7`}
                className={`w-3 h-3 sm:w-3.5 sm:h-3.5 rounded-full transition-all ${
                  readOnly ? 'cursor-default' : 'cursor-pointer'
                } ${
                  isMine ? 'ring-2 ring-offset-1 ring-charcoal/40' : ''
                } ${
                  selected
                    ? 'bg-orange-primary scale-125 shadow-sm'
                    : isSaving
                      ? 'bg-orange-primary/40'
                      : readOnly
                        ? 'bg-charcoal/10'
                        : 'bg-charcoal/15 hover:bg-orange-primary/40'
                }`}
                style={isMine ? { '--tw-ring-offset-color': '#FFF8F0' } : undefined}
              />
            );
          })}
        </div>

        <span className="flex-1 text-xs sm:text-sm text-charcoal/70 text-left leading-snug">
          {right}
        </span>
      </div>

      {!readOnly && (
        <div className="flex items-center justify-center gap-3 mt-2 min-h-[18px]">
          <button
            type="button"
            onClick={handleSkip}
            data-testid={`scale-${id}-skip`}
            className="text-xs text-charcoal/35 hover:text-charcoal/70 transition-colors underline-offset-2 hover:underline"
          >
            {value === null || value === undefined ? 'Skipped' : 'Skip this one'}
          </button>
          <AnimatePresence>
            {justSaved && (
              <motion.span
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="text-xs text-green-600"
              >
                Saved
              </motion.span>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};
